/* global window */

/**
 * IMPORTS
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
} from 'reactstrap';
import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faTimes,
  faCheck,
  faPlay,
  faBars,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import { Translate } from 'react-i18nify';
import PinchZoomPan from 'react-responsive-pinch-zoom-pan';
import 'weathericons/css/weather-icons.min.css';

import packageJson from '../../../package.json';

import {
  completeStep,
  cancelStep,
} from '../../actions/nav-actions';
import { setLocale } from '../../actions/app-actions';
import {
  getStepDisabled,
  getWeather,
  getRequestingStatus,
  getAllProjects,
  getDemoUrl,
  getCameraLocationUrl,
  getCurrentProjectStatus,
} from '../../selectors';
import getTranslations from '../../utils/getTranslations';
import transformStatus from '../../utils/transformProjectStatus';

import logo from '../../assets/logo.png';

/**
 * CORE
 */

function onLogoClick() {
  const reload = window.confirm(`Do you want to reload the app (v${packageJson.version})?`);
  if (reload) {
    window.location.reload();
  }
}

class Home extends Component {
  static stepId = 'home';

  static propTypes = {
    weather: PropTypes.object,
    shouldReload: PropTypes.bool,
    isRequesting: PropTypes.bool,
    disabled: PropTypes.bool,
    videoDemo: PropTypes.string,
    cameraLocation: PropTypes.string,
    canPickProjects: PropTypes.bool,
    completeStep: PropTypes.func.isRequired,
    cancelStep: PropTypes.func.isRequired,
    setLocale: PropTypes.func.isRequired,
    projectStatus: PropTypes.object.isRequired,
  };

  static defaultProps = {
    disabled: false,
    weather: null,
    shouldReload: false,
    isRequesting: false,
    videoDemo: undefined,
    cameraLocation: undefined,
    canPickProjects: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      weatherModal: false,
      videoModal: false,
      cameraLocationModal: false,
      translations: null,
      showProjectStatus: false,
      projectStatusMessageClassName: 'hide',
      projectStatusDotClassName: 'small',
    };

    this.onButtonClick = this.onButtonClick.bind(this);
    this.onBackButtonClick = this.onBackButtonClick.bind(this);
    this.onWeatherToggle = this.onWeatherToggle.bind(this);
    this.onVideoModalToggle = this.onVideoModalToggle.bind(this);
    this.onCameraLocationModalToggle = this.onCameraLocationModalToggle.bind(this);
    this.renderProjectStatus = this.renderProjectStatus.bind(this);
    this.toggleShowProjectStatus = this.toggleShowProjectStatus.bind(this);
  }

  async componentWillMount() {
    const translations = await getTranslations();
    this.setState({ translations });
  }

  onButtonClick(locale) {
    this.props.setLocale(locale);

    const { weather } = this.props;
    if (weather && weather.warning) {
      this.setState({ weatherModal: true });
    } else {
      this.props.completeStep(Home.stepId);
    }
  }

  onBackButtonClick() {
    this.props.cancelStep(Home.stepId);
  }

  onWeatherToggle() {
    const { weatherModal } = this.state;
    this.setState({ weatherModal: !weatherModal });
  }

  onVideoModalToggle() {
    const { videoModal } = this.state;
    this.setState({ videoModal: !videoModal });
  }

  onCameraLocationModalToggle() {
    const { cameraLocationModal } = this.state;
    this.setState({ cameraLocationModal: !cameraLocationModal });
  }

  toggleShowProjectStatus() {
    const { showProjectStatus } = this.state;
    this.setState({
      showProjectStatus: !showProjectStatus,
      projectStatusMessageClassName: showProjectStatus
        ? 'scale-out-hor-left'
        : 'scale-in-hor-left',
      projectStatusDotClassName: showProjectStatus
        ? 'scale-out-center-50'
        : 'scale-in-center-50',
    });
  }

  renderButton(locale, isReady) {
    const { disabled } = this.props;
    const { translations } = this.state;
    if (!translations) {
      return null;
    }

    const text = translations[locale].Home.take_selfie;
    return (
      <Button
        className="app-button"
        color="primary"
        size="lg"
        onClick={() => this.onButtonClick(locale)}
        disabled={disabled || !isReady}
      >
        <div className="clearfix">
          <span>{text}</span>
          <span className="ml-2 float-right">
            <FA icon={faArrowRight} />
          </span>
        </div>
      </Button>
    );
  }

  renderDemoButton() {
    return (
      <Button
        color="primary"
        size="lg"
        className="bottom-button demo-button"
        onClick={this.onVideoModalToggle}
      >
        <span>
          <FA icon={faPlay} fixedWidth />
          &nbsp;
          <Translate value="Home.demo" />
        </span>
      </Button>
    );
  }

  renderCameraLocationButton() {
    return (
      <Button
        color="primary"
        size="lg"
        className="bottom-button camera-button"
        onClick={this.onCameraLocationModalToggle}
      >
        <span>
          <FA icon={faEye} fixedWidth />
          &nbsp;
          <Translate value="Home.location" />
        </span>
      </Button>
    );
  }

  renderBackButton() {
    return (
      <Button
        color="primary"
        className="bottom-button back-button"
        onClick={this.onBackButtonClick}
      >
        <span>
          <FA icon={faBars} fixedWidth />
          &nbsp;
          <Translate value="Home.back" />
        </span>
      </Button>
    );
  }

  renderVideoModal() {
    const { videoDemo } = this.props;
    const { videoModal } = this.state;
    return (
      <Modal
        size="xl"
        className="transparent-modal"
        isOpen={videoModal}
        toggle={this.onVideoModalToggle}
        centered
      >
        <ModalBody style={{ padding: 0 }}>
          <button
            type="button"
            onClick={this.onVideoModalToggle}
            className="close-modal-button"
          >
            <FA icon={faTimes} />
          </button>
          <video
            preload="auto"
            style={{
              width: '100%',
              height: '100%',
            }}
            controls={false}
            autoPlay
            loop
            muted
          >
            <source
              src={videoDemo}
              type="video/mp4"
            />
          </video>
        </ModalBody>
      </Modal>
    );
  }

  renderCameraLocationModal() {
    const { cameraLocation } = this.props;
    const { cameraLocationModal } = this.state;
    return (
      <Modal
        size="xl"
        className="transparent-modal"
        isOpen={cameraLocationModal}
        toggle={this.onCameraLocationModalToggle}
        centered
      >
        <ModalBody style={{ padding: 0 }}>
          <button
            type="button"
            onClick={this.onCameraLocationModalToggle}
            className="close-modal-button"
          >
            <FA icon={faTimes} />
          </button>
          <div
            className="pinch-zoom-pan-container"
          >
            <PinchZoomPan
              position="center"
              zoomButtons={false}
            >
              <img
                alt="Camera Location"
                src={cameraLocation}
                style={{ pointerEvents: 'all' }}
              />
            </PinchZoomPan>
          </div>
        </ModalBody>
      </Modal>
    );
  }

  renderWeatherModal() {
    const { weather, disabled } = this.props;
    return (
      <Modal
        className="modal-dialog-centered"
        isOpen={this.state.weatherModal}
        toggle={this.onWeatherToggle}
      >
        <ModalBody
          className="d-flex justify-content-between align-items-center"
        >
          <div
            className="text-primary mr-3"
            style={{ fontSize: '4rem' }}
          >
            <i className={`wi wi-yahoo-${weather.code}`} />
          </div>
          <Translate value="Home.weather.warning" />
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            className="app-button"
            onClick={this.onWeatherToggle}
            disabled={disabled}
            outline
          >
            <FA icon={faTimes} />
            &nbsp;
            <Translate value="Home.weather.close" />
          </Button>
          <Button
            color="primary"
            className="app-button"
            onClick={() => this.props.completeStep(Home.stepId)}
            disabled={disabled}
          >
            <FA icon={faCheck} />
            &nbsp;
            <Translate value="Home.weather.continue" />
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  renderProjectStatus() {
    const { projectStatus } = this.props;
    const { projectStatusDotClassName, projectStatusMessageClassName } = this.state;
    const { level, message } = transformStatus(projectStatus);
    return (
      <div>
        <div
          className="project-status-dot-holder"
          onClick={this.toggleShowProjectStatus}
        >
          <div
            className={`project-status-dot ${level} ${projectStatusDotClassName}`}
            onClick={this.toggleShowProjectStatus}
          />
        </div>
        <div
          className={`project-status-message ${level} ${projectStatusMessageClassName}`}
          onClick={this.toggleShowProjectStatus}
        >
          <span>
            {message}
          </span>
        </div>
      </div>
    );
  }

  render() {
    const {
      shouldReload,
      isRequesting,
      videoDemo,
      cameraLocation,
      canPickProjects,
      projectStatus,
    } = this.props;
    const { translations } = this.state;
    const isReady = projectStatus.isOK;

    if (shouldReload && !isRequesting) {
      return (
        <Card className="text-center">
          <CardBody>
            <div>An update is available</div>
            <div>
              <Button
                className="app-button"
                color="primary"
                onClick={() => window.location.reload()}
              >
                Reload
              </Button>
            </div>
          </CardBody>
        </Card>
      );
    }

    return (
      <Fragment>
        {this.renderWeatherModal()}
        {this.renderProjectStatus()}
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: 70 }}>
            <img
              src={logo}
              alt="logo"
              className="logo"
              onClick={onLogoClick}
            />
          </div>
          <div>
            {Object.keys(translations || {}).map(l => (
              <div key={l}>{this.renderButton(l, isReady)}</div>
            ))}
          </div>
          <div className="bottom-buttons-holder">
            {!!videoDemo && (
              <Fragment>
                {this.renderDemoButton()}
                {this.renderVideoModal()}
              </Fragment>
            )}
            {!!cameraLocation && (
              <Fragment>
                {this.renderCameraLocationButton()}
                {this.renderCameraLocationModal()}
              </Fragment>
            )}
            {canPickProjects && this.renderBackButton()}
          </div>
        </div>
      </Fragment>
    );
  }
}

export default connect(
  state => ({
    disabled: getStepDisabled(state),
    weather: getWeather(state),
    isRequesting: getRequestingStatus(state),
    shouldReload: !!window.shouldReloadApp,
    videoDemo: getDemoUrl(state),
    cameraLocation: getCameraLocationUrl(state),
    canPickProjects: (getAllProjects(state) || []).length > 1,
    projectStatus: getCurrentProjectStatus(state),
  }),
  {
    completeStep,
    cancelStep,
    setLocale,
  },
)(Home);
